import React, { memo } from 'react';
import { ChevronRight, FileText, Star, TrendingUp } from 'lucide-react';
import type { SuggestionItem, ProcessedSuggestion } from '../../types/chat';

interface SuggestionCardProps {
  suggestion: SuggestionItem | ProcessedSuggestion;
  onSelect: (suggestion: SuggestionItem) => void;
  showSnippet?: boolean;
  enhanced?: boolean;
  className?: string;
}

export const SuggestionCard: React.FC<SuggestionCardProps> = memo(({
  suggestion,
  onSelect,
  showSnippet = false,
  enhanced = false,
  className = ''
}) => {
  const handleClick = () => {
    onSelect(suggestion);
  };

  const isProcessedSuggestion = (s: any): s is ProcessedSuggestion => {
    return 'isHighConfidence' in s && 'displayCategory' in s;
  };

  const processedSuggestion = isProcessedSuggestion(suggestion) ? suggestion : null;
  const isHighConfidence = processedSuggestion?.isHighConfidence || suggestion.confidence >= 0.8;
  const displayCategory = processedSuggestion?.displayCategory || suggestion.category || 'Thủ tục hành chính';

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return 'text-green-600 bg-green-50 border-green-200';
    if (confidence >= 0.6) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getCardBorderColor = () => {
    if (!enhanced) return 'border-gray-200 hover:border-blue-300';
    if (isHighConfidence) return 'border-green-200 hover:border-green-400';
    return 'border-gray-200 hover:border-blue-300';
  };

  const getCardBackground = () => {
    if (!enhanced) return 'bg-white hover:bg-blue-50';
    if (isHighConfidence) return 'bg-white hover:bg-green-50';
    return 'bg-white hover:bg-blue-50';
  };

  return (
    <div
      className={`
        group relative w-full p-4 border rounded-lg
        cursor-pointer transition-all duration-200 
        hover:shadow-md transform hover:scale-[1.02]
        ${getCardBorderColor()}
        ${getCardBackground()}
        text-gray-800
        ${className}
      `}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      aria-label={`Select suggestion: ${suggestion.title}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Icon with confidence indicator */}
          <div className="flex-shrink-0 mt-0.5 relative">
            <FileText className={`h-4 w-4 ${isHighConfidence && enhanced ? 'text-green-600' : 'text-blue-600'}`} />
            {enhanced && isHighConfidence && (
              <div className="absolute -top-1 -right-1">
                <Star className="h-2.5 w-2.5 text-yellow-500 fill-current" />
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1">
              <h3 className="text-base font-medium text-gray-900 leading-relaxed flex-1">
                {suggestion.title}
              </h3>
              {enhanced && (
                <div className="flex items-center gap-1">
                  <TrendingUp className="h-3 w-3 text-gray-400" />
                  <span className="text-xs text-gray-500 font-medium">
                    {Math.round(suggestion.confidence * 100)}%
                  </span>
                </div>
              )}
            </div>
            
            {/* Show snippet if enabled and available */}
            {showSnippet && suggestion.snippet && (
              <p className="text-xs text-gray-600 leading-relaxed line-clamp-2 mb-2">
                {suggestion.snippet}
              </p>
            )}
            
            {/* Enhanced metadata row */}
            <div className="flex items-center justify-between gap-2 mt-2">
              {/* Category badge */}
              <span className={`
                inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                ${enhanced && isHighConfidence 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-blue-100 text-blue-800'
                }
              `}>
                {displayCategory}
              </span>
              
              {/* Confidence indicator for enhanced mode */}
              {enhanced && (
                <div className={`
                  inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border
                  ${getConfidenceColor(suggestion.confidence)}
                `}>
                  {isHighConfidence ? 'High Match' : 'Related'}
                </div>
              )}
            </div>

            {/* MA_TTHC code for enhanced mode */}
            {enhanced && suggestion.ma_tthc && (
              <div className="mt-2">
                <span className="text-xs text-gray-500 font-mono">
                  ID: {suggestion.ma_tthc}
                </span>
              </div>
            )}
          </div>
        </div>
        
        {/* Arrow icon */}
        <div className="flex-shrink-0">
          <ChevronRight className={`
            h-4 w-4 transition-colors duration-200
            ${enhanced && isHighConfidence 
              ? 'text-green-400 group-hover:text-green-600' 
              : 'text-gray-400 group-hover:text-blue-600'
            }
          `} />
        </div>
      </div>
    </div>
  );
});

export default SuggestionCard;